import * as moment from 'moment';
import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BreadcrumbsService } from '../../core/breadcrumbs.service';
import { ScrollService } from '../../core/scroll.service';
import { LangService } from '../../core/lang.service';
import { Subscription } from 'rxjs';
import { ChecklistStep, ChecklistStepRoute } from '../pnel-cts-checklist/model/checklist-steps';
import { IAccountType } from '../../constants/account-types';
import { LoginGuardService } from '../../api/login-guard.service';
import { MyTestSessionsSetupService, ILocation, SORT_BY_DATETIMESTART } from '../my-test-sessions-setup.service';

const FMT_DATE_MONTH_DISPLAY = 'MMMM YYYY';
const FMT_DATE_DAY_DISPLAY = 'MMM. D, YYYY (dddd)';
const FMT_DATE_TIME_DISPLAY = 'H:mm a';

interface ISessionsMonth {
    monthDisplay: string,
    days: ISessionsDay[]
}
interface ISessionsDay {
  dayDisplay: string,
  isExpanded:boolean,
  sessions:ISession[]
}

interface ISession {
  m: moment.Moment;
  id:number,
  locationId:number,
  sessionNum:number,
  hasAccessCode: boolean, 
  isHidden: boolean, 
  timeFrom: string,
  institutionName?: string,
  testLocation?: string,
}



@Component({
  selector: 'view-cts-make-available',
  templateUrl: './view-cts-make-available.component.html',
  styleUrls: ['./view-cts-make-available.component.scss']
})
export class ViewCtsMakeAvailableComponent implements OnInit {

  public breadcrumb = [];
  public setupId:string;

  public sessionMonths:ISessionsMonth[] = [];
  public isLoaded:boolean;
  public isLoadFailed:boolean;
  
  private locations:Map<number, ILocation>;
  private allSessions:ISession[] = [];
  private mySetupSub:Subscription;
  private routeSub:Subscription;

  constructor(
    private loginGuard: LoginGuardService, // 
    private breadcrumbsService: BreadcrumbsService,
    private router:Router,
    private route:ActivatedRoute,
    private scrollService:ScrollService,
    private lang:LangService,
    private mySetup:MyTestSessionsSetupService,
  ) { }
  
  ngOnInit() {
    this.loginGuard.activate([IAccountType.TEST_ADMIN]);
    this.scrollService.scrollToTop();
    this.routeSub = this.route.params.subscribe(routeParams => {
      this.setupId = routeParams['setupId'];
      this.breadcrumb = this.mySetup.generateBreadcrumbs(this.setupId, 'available_title');
    });
    this.initRouteView();
  }

  initRouteView(){
    let isInitialized = false;
    this.mySetupSub = this.mySetup.sub().subscribe(setupInfo => {
      if (setupInfo && !isInitialized){
        isInitialized = true;
        this.initLocations().then( ()=> {
          return this.initLocationTimeSlots();
        })
      }
    })
  }


  initLocations(){
    return this.mySetup
      .getLocations(<any>this.setupId)
      .then(locationsData => {
        this.locations = new Map()
        locationsData.forEach(location => {
          this.locations.set(location.id, location);
        })
      })
      .catch(e => {
        this.isLoadFailed = true;
      })
  }
  
  initLocationTimeSlots(){
    this.isLoaded = true;
    const setupId = parseInt(this.setupId);
    this.mySetup
      .getTimeSlots(setupId)
      .then( timeSlots => {
        timeSlots = timeSlots.sort(SORT_BY_DATETIMESTART);
        this.allSessions = [];
        this.sessionMonths = [];
        let currentMonthDisplay : string;
        let currentDayDisplay : string;
        let currentMonth:ISessionsMonth;
        let currentDay:ISessionsDay;
        timeSlots.forEach( (timeSlot, timeSlotIndex) => {
          const m = moment(timeSlot.dateTimeStart);
          const monthDisplay = m.format(FMT_DATE_MONTH_DISPLAY);
          const dayDisplay = m.format(FMT_DATE_DAY_DISPLAY);
          const timeDisplay = m.format(FMT_DATE_TIME_DISPLAY);
          if (currentMonthDisplay !== monthDisplay){
            currentDayDisplay = null;
            currentMonthDisplay = monthDisplay;
            currentMonth = { monthDisplay, days: [], }
            this.sessionMonths.push(currentMonth);
          }
          if (currentDayDisplay !== dayDisplay){
            currentDayDisplay = dayDisplay;
            currentDay = { dayDisplay, isExpanded: false, sessions: [] };
            currentMonth.days.push(currentDay);
          }
          const session = {
            m,
            id: timeSlot.setupId,
            locationId: timeSlot.locationId,
            sessionNum: timeSlotIndex+1,
            hasAccessCode: timeSlot.isAccessCodeProtected, 
            isHidden: timeSlot.isHidden, 
            timeFrom: timeDisplay,
            institutionName: this.renderLocationInst(),
            testLocation: this.renderLocationAddress(timeSlot.locationId),
          }
          currentDay.sessions.push(session)
          this.allSessions.push(session)
        })
      });
  }

  

  renderLocationInst(){
    return this.mySetup.getInstName()
  }

  renderLocationAddress(locationId:number){
    const loc = this.locations.get(locationId);
    return [
      ( this.mySetup.getRoomCaption() +', '+ loc.room +', '+ loc.campusBuilding ),
      ( loc.address ),
      ( loc.city +', '+ loc.province +' '+ loc.postalCode ),
    ].join('\n');
  }

  toggleDayExpanded(day:ISessionsDay){
    day.isExpanded = !day.isExpanded;
  }

  isReadyToMakeAvail(){
    return this.mySetup.isReadyToMakeAvail();
  }

  getCurrentStepId(){
    return ChecklistStep.MakeSessionsAvailable
  }

  isSessionsReleased(){
    return this.mySetup.isSessionsReleased();
  }

  makeSessionsAvailable(){
    this.mySetup.markSessionsAsReleased_sim(this.allSessions);
    setTimeout(() => {
      this.gotoNextRoute();
    }, 1000)
  }

  gotoNextRoute(){
    const nextRouteSuffix = ChecklistStepRoute.SendInvitations;
    let breadcrumb = this.breadcrumbsService.TESTADMIN_CREATE_TEST_SESSION(this.setupId, nextRouteSuffix);
    this.router.navigate([ breadcrumb.route ]);
  }

}
